import React from 'react';
import {
    Box,
    Paper,
    Typography,
    Grid,
    Card,
    CardContent,
    CircularProgress,
    Chip,
} from '@mui/material';
import {
    CheckCircle as HealthyIcon,
    Error as UnhealthyIcon,
    Warning as WarningIcon,
    Storage as DatabaseIcon,
    Email as EmailIcon,
    PhoneAndroid as PhoneAndroidIcon,
    RecordVoiceOver as RecordVoiceOverIcon,
    Router as RouterIcon,
    Tv as TvIcon,
    Info as InfoIcon,
    HelpOutline as UnknownIcon,
} from '@mui/icons-material';
import { PageHeader } from '../../components/admin';
import { useSystemHealthQuery } from '../../api/JsdhNotifikaceService/service/AdminMutations';
import type { ComponentHealth } from '../../api/JsdhNotifikaceService/generated/models/ComponentHealth';

const translateStatus = (status: string): string => {
    switch (status.toUpperCase()) {
        case 'UP':
            return 'V pořádku';
        case 'DOWN':
            return 'Nefunkční';
        case 'DEGRADED':
            return 'Zhoršený';
        case 'UNKNOWN':
            return 'Neznámý';
        default:
            return status;
    }
};

const StatusIcon: React.FC<{ status: string; size?: 'small' | 'large' }> = ({ status, size = 'small' }) => {
    const fontSize = size === 'large' ? 40 : 24;
    switch (status.toUpperCase()) {
        case 'UP':
            return <HealthyIcon color="success" sx={{ fontSize }} />;
        case 'DOWN':
            return <UnhealthyIcon color="error" sx={{ fontSize }} />;
        case 'UNKNOWN':
            return <UnknownIcon color="disabled" sx={{ fontSize }} />;
        default:
            return <WarningIcon color="warning" sx={{ fontSize }} />;
    }
};

const StatusChip: React.FC<{ status: string }> = ({ status }) => {
    const color = status.toUpperCase() === 'UP' ? 'success' : status.toUpperCase() === 'DOWN' ? 'error' : 'warning';
    return <Chip label={translateStatus(status)} color={color} size="small" />;
};

const componentIcons: Record<string, React.ReactNode> = {
    'Database': <DatabaseIcon color="primary" />,
    'Email': <EmailIcon color="primary" />,
    'FCM': <PhoneAndroidIcon color="primary" />,
    'TTS': <RecordVoiceOverIcon color="primary" />,
    'MQTT Gateway': <RouterIcon color="primary" />,
    'Devices': <TvIcon color="primary" />,
};

const formatDetailValue = (key: string, value: unknown, details: Record<string, unknown>): string | null => {
    switch (key) {
        case 'users_in_database':
            return `${value} uživatelů`;
        case 'mail_username':
            return value ? String(value) : null;
        case 'active_tokens':
            return `${value} tokenů`;
        case 'online_count':
            return `${value} online / ${details.total_count ?? '?'} celkem`;
        case 'last_seen':
            if (!value) return null;
            try {
                return 'Poslední: ' + new Date(String(value)).toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' });
            } catch {
                return 'Poslední: ' + String(value);
            }
        case 'client_status':
            return String(value);
        default:
            return null;
    }
};

const getDisplayDetails = (component: ComponentHealth): string[] => {
    if (!component.details) return [];
    const lines: string[] = [];
    const details = component.details;

    // Show responseTime if present
    if (component.responseTime != null && component.responseTime > 0) {
        lines.push(`${component.responseTime} ms`);
    }

    // Skip internal/duplicate keys
    const skipKeys = new Set(['connection_valid', 'mail_sender_configured', 'error', 'enabled', 'total_count', 'registration_enabled', 'http_status']);

    for (const [key, value] of Object.entries(details)) {
        if (skipKeys.has(key)) continue;
        const formatted = formatDetailValue(key, value, details);
        if (formatted) {
            lines.push(formatted);
        }
    }

    return lines;
};

const ComponentCard: React.FC<{
    component: ComponentHealth;
    isLoading: boolean;
}> = ({ component, isLoading }) => {
    const icon = componentIcons[component.name] ?? <InfoIcon color="primary" />;
    const displayDetails = getDisplayDetails(component);

    return (
        <Card sx={{ height: '100%' }}>
            <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Box display="flex" alignItems="center" gap={1}>
                        {icon}
                        <Typography variant="h6" fontSize="1rem">{component.name}</Typography>
                    </Box>
                    {isLoading ? (
                        <CircularProgress size={20} />
                    ) : (
                        <StatusChip status={component.status} />
                    )}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    {component.message}
                </Typography>
                {displayDetails.map((line, i) => (
                    <Typography key={i} variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                        {line}
                    </Typography>
                ))}
            </CardContent>
        </Card>
    );
};

const HealthPage: React.FC = () => {
    const { data: systemHealth, isLoading: systemLoading } = useSystemHealthQuery();

    return (
        <Box>
            <PageHeader
                title="Zdraví systému"
                subtitle={`Celkový stav: ${systemHealth?.overallStatus ? translateStatus(systemHealth.overallStatus) : 'Načítání...'}`}
                icon={
                    systemLoading ? (
                        <CircularProgress size={40} />
                    ) : (
                        <StatusIcon status={systemHealth?.overallStatus || 'UNKNOWN'} size="large" />
                    )
                }
                action={
                    <>
                        {systemHealth?.overallStatus && (
                            <StatusChip status={systemHealth.overallStatus} />
                        )}
                        {systemHealth?.timestamp && (
                            <Typography variant="body2" color="text.secondary">
                                Poslední kontrola:{' '}
                                {new Date(systemHealth.timestamp).toLocaleTimeString('cs-CZ', {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    second: '2-digit',
                                })}
                            </Typography>
                        )}
                    </>
                }
            />

            {/* Dynamic component cards */}
            <Grid container spacing={2}>
                {systemLoading && !systemHealth ? (
                    <Grid size={{ xs: 12 }}>
                        <Box display="flex" justifyContent="center" p={4}>
                            <CircularProgress />
                        </Box>
                    </Grid>
                ) : (
                    systemHealth?.components?.map((component, index) => (
                        <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={component.name || index}>
                            <ComponentCard component={component} isLoading={systemLoading} />
                        </Grid>
                    ))
                )}
            </Grid>

            {/* System Info - compact */}
            {systemHealth?.systemInfo && Object.keys(systemHealth.systemInfo).length > 0 && (
                <Paper sx={{ p: 2, mt: 3 }}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <InfoIcon color="primary" fontSize="small" />
                        <Typography variant="subtitle2">Systémové informace</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                        Java {String(systemHealth.systemInfo.java_version)}
                        {' · '}
                        {String(systemHealth.systemInfo.os_name)}
                        {' · '}
                        {String(systemHealth.systemInfo.processors)} CPU
                        {' · '}
                        {String(systemHealth.systemInfo.used_memory_mb)}/{String(systemHealth.systemInfo.max_memory_mb)} MB RAM
                    </Typography>
                </Paper>
            )}
        </Box>
    );
};

export default HealthPage;
